import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { debounce } from 'lodash'

import { CREATE_AI_TEST_TRAINING_DATA } from 'queries/trainingData'
import { AITestTypeEnum } from '__generatedTypes__/globalTypes'
import {
  createAiTestTrainingData,
  createAiTestTrainingDataVariables,
} from '__generatedTypes__/createAiTestTrainingData'
import { toast } from 'utils/toast'
import { FormValues } from '../Form'
import { useGradeEdit } from '../hooks'

export const useTrainingGradeEdit = () => {
  const gradeEdit = useGradeEdit(false)

  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)

  const [createTrainingData] = useMutation<createAiTestTrainingData, createAiTestTrainingDataVariables>(
    CREATE_AI_TEST_TRAINING_DATA
  )

  const handleCreateSubmit = async (values: FormValues) => {
    if (!gradeEdit.questionId) return

    try {
      await createTrainingData({
        variables: {
          input: {
            answerText: values.response,
            quartile: Number(values.quartile.id),
            quartileExplanation: values.quartileExplanation,
            responseFeedback: values.responseFeedback,
            question: { id: gradeEdit.questionId },
            testType: AITestTypeEnum.TextTest,
            isFromAI: false,
          },
        },
      })

      await gradeEdit.refetch()
      setCreating(false)

      toast.success('Training data created successfully')
    } catch (e) {
      toast.error('Failed to create training data')
    }
  }

  const handleAddContent = () => {
    setCreating(true)
  }

  const handleCreateCancel = () => {
    setCreating(false)
  }

  const debouncedSearch = useMemo(() => debounce(gradeEdit.search, 500), [])

  const handleSearch = (value: string) => {
    setSearch(value)
    if (!value) {
      gradeEdit.search()
      return
    }

    debouncedSearch(value)
  }

  const trainingData = gradeEdit.trainingData

  return {
    ...gradeEdit,
    trainingData,
    creating,
    handleCreateSubmit,
    handleAddContent,
    handleCreateCancel,
    search,
    handleSearch,
  }
}
